import React, { FC } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import type { AnalysisParameters, GradingItem } from "../../../../hooks";

import { Button, TextField } from "@components/base";

interface GradingRowsFormProps {
  grading: GradingItem[];
  gradingKey: "grading1" | "grading2" | "grading3";
  parameters: AnalysisParameters;
  maxRows: number;
  onGradingChange: (index: number, field: keyof GradingItem, value: string) => void;
  onAddGrading: () => void;
  onRemoveGrading: (index: number) => void;
}

export const GradingRowsForm: FC<GradingRowsFormProps> = ({
  grading,
  gradingKey,
  parameters,
  maxRows,
  onGradingChange,
  onAddGrading,
  onRemoveGrading,
}) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {grading.map((item, index) => (
        <Box
          key={`grading-row-${index}`}
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "120px 1fr 64px" }}
          gap={1}
          alignItems="center"
        >
          <TextField
            label={t("paygap.referentials.id")}
            type="number"
            value={item.id}
            error={
              !!item.id.trim() &&
              parameters[gradingKey].filter(
                (row) => row.id.trim() === item.id.trim(),
              ).length > 1
            }
            helperText={
              !!item.id.trim() &&
              parameters[gradingKey].filter(
                (row) => row.id.trim() === item.id.trim(),
              ).length > 1
                ? t("paygap.referentials.uniqueId")
                : undefined
            }
            onChange={(e) => onGradingChange(index, "id", e.target.value)}
            fullWidth
          />
          <TextField
            label={t("paygap.referentials.name")}
            value={item.name}
            onChange={(e) => onGradingChange(index, "name", e.target.value)}
            fullWidth
          />
          <IconButton
            aria-label={t("paygap.referentials.remove")}
            onClick={() => onRemoveGrading(index)}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="outlined"
          onClick={onAddGrading}
          disabled={grading.length >= maxRows}
        >
          {t("paygap.referentials.addRow")}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {`${grading.length}/${maxRows}`} ·{" "}
          {t("paygap.referentials.gradingLimit")}
        </Typography>
      </Box>
    </Box>
  );
};
